/**
 * WorkspaceFocusChip — tiny top-nav chip that lets a shared workspace
 * user pick their lane. Tracy handles Dispo; her partner handles
 * Acquisitions. The chip persists to `/api/v3/beta/focus` and reshapes
 * their sidebar highlighting via a CSS attribute on <html>.
 *
 * This is a UI hint, not a security boundary — every user still has
 * full access to every page.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, Target } from "lucide-react";

const OPTIONS = [
  { key: "both",         label: "Both lanes",   sub: "See everything" },
  { key: "acquisitions", label: "Acquisitions", sub: "Discover · Call · Contract" },
  { key: "dispo",        label: "Dispositions", sub: "Buyers · TC · Closings" },
];

export default function WorkspaceFocusChip() {
  const [focus, setFocus] = useState("both");
  const [open, setOpen]   = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/v3/beta/focus")
      .then((r) => {
        if (!alive) return;
        const f = r.data?.focus || "both";
        setFocus(f);
        document.documentElement.setAttribute("data-focus", f);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const pick = useCallback(async (key) => {
    setFocus(key); setOpen(false);
    document.documentElement.setAttribute("data-focus", key);
    try { await api.put("/v3/beta/focus", { focus: key }); }
    catch { /* silent — the UI already flipped */ }
  }, []);

  const cur = OPTIONS.find((o) => o.key === focus) || OPTIONS[0];

  return (
    <div className="relative" data-testid="workspace-focus-chip">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:border-indigo-400 text-[11.5px] font-bold px-2.5 py-1 text-slate-700"
        title="Change workspace focus"
      >
        <Target className="w-3 h-3 text-indigo-500" />
        <span className="uppercase tracking-wider">{cur.label}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1.5 z-[61] w-56 rounded-xl border border-slate-200 bg-white shadow-xl p-1">
            {OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => pick(o.key)}
                className={`w-full text-left rounded-lg px-2.5 py-2 hover:bg-indigo-50 ${
                  focus === o.key ? "bg-indigo-50" : ""
                }`}
                data-testid={`focus-opt-${o.key}`}
              >
                <div className="text-[13px] font-black text-slate-900">{o.label}</div>
                <div className="text-[11px] text-slate-500">{o.sub}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
